import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';

import {DEVICE} from 'src/app/shared/domain/tokens/device.token';
import {environment} from 'src/environments/environment';
import {IBookingCheckStatusResponse} from '../../domain/interfaces/booking-check-status-response.interface';
import {IBookingGetAllParams} from '../../domain/interfaces/booking-get-all-params.payload';
import {IBookingUnLockRequestPayload} from '../../domain/interfaces/booking-unlock-request.payload';
import {IBookingUnLockResponse} from '../../domain/interfaces/booking-unlock-response.interface';
import {Booking} from '../../domain/models/booking.model';
import {BookingRepository} from '../../domain/repositories/booking.repository';
import {BOOKING_SESSION} from '../../domain/tokens/booking-session.token';
import {BookingActivatePayloadBuilder} from '../builders/booking-activate-payload.builder';
import {IApiBooking} from '../interfaces/booking-api.interface';
import {BookingMapper} from '../mappers/booking.mapper';
import {BaseHttpService} from '../../../../../shared/services/http/base-http.service';
import {ICloseBookingPayload} from "../../application/builders/close-booking-payload.builder";

@Injectable({providedIn: 'root'})
export class BookingRepositoryAdapter
  extends BaseHttpService
  implements BookingRepository {
  private readonly activatePayloadBuilder = inject(
    BookingActivatePayloadBuilder
  );
  private readonly bookingSession = inject(BOOKING_SESSION);
  private readonly deviceService = inject(DEVICE);

  readonly baseUrl = `${environment.apiURL}/api/v2/bookings`;

  getAll(params?: IBookingGetAllParams): Observable<Booking[]> {
    return this.get<IApiBooking[]>('detailed-search', params?.queryParams).pipe(
      map((response) => this.mapApiBookingsToDomain(response)),
      catchError(() => of([]))
    );
  }

  getActive(): Observable<Booking | null> {
    return this.deviceService.getDeviceInfoAsync().pipe(
      switchMap((deviceInfo) => {
        if (!deviceInfo) {
          return of(null);
        }

        return this.bookingSession.get().pipe(
          switchMap((bookingId: string | null) => {
            if (!bookingId) {
              return of(null);
            }

            return this.get<IApiBooking>(`detailed-find/${bookingId}?deviceId=${deviceInfo.id}`).pipe(
              map(response => this.mapApiBookingToDomain(response)),
              catchError(() => of(null))
            );
          })
        );
      })
    );
  }

  activate(bookingId: string): Observable<Booking | null> {
    const payload = this.activatePayloadBuilder.build(bookingId);

    const saveBookingId$ = this.bookingSession.save(bookingId);

    return this.post<IApiBooking>(`set-active`, payload).pipe(
      map((response) => this.mapApiBookingToDomain(response)),
      switchMap((booking) => saveBookingId$.pipe(map(() => booking))),
      catchError(() => of(null))
    );
  }

  close(payload: ICloseBookingPayload): Observable<void> {
    return this.post<void>('release', payload);
  }

  unlock(
    payload: IBookingUnLockRequestPayload
  ): Observable<IBookingUnLockResponse> {
    return this.post<IBookingUnLockResponse>('unlock', payload).pipe(
      catchError(() => of({unlocked: false} satisfies IBookingUnLockResponse))
    );
  }

  checkStatus(bookingId: string): Observable<IBookingCheckStatusResponse> {
    const deviceId = this.deviceService.getDeviceInfo()?.id;

    if (!deviceId) {
      return of({deviceName: ''} satisfies IBookingCheckStatusResponse);
    }

    return this.post<IBookingCheckStatusResponse>('check-status', {bookingId, deviceId}).pipe(
      catchError(() => of({deviceName: ''} satisfies IBookingCheckStatusResponse))
    );
  }

  private mapApiBookingToDomain(apiBooking: IApiBooking): Booking {
    return BookingMapper.fromJSON(apiBooking);
  }

  private mapApiBookingsToDomain(apiBookings: IApiBooking[]): Booking[] {
    return apiBookings.map((booking) => BookingMapper.fromJSON(booking));
  }
}
